import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { getFacilityQrScanPath } from "lib/utils/facilityQr";

export default function FacilityQrScanner({ t, onScan, onClose }) {
  const videoRef = useRef(null);
  const [message, setMessage] = useState("cameraStarting");

  useEffect(() => {
    let active = true;
    let stream;
    let frame;
    let lastRead = 0;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    const stop = () => {
      active = false;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach((track) => track.stop());
      if (video) video.srcObject = null;
    };
    const readFrame = (time) => {
      if (!active) return;
      if (video.readyState >= 2 && video.videoWidth && time - lastRead >= 200) {
        lastRead = time;
        const scale = Math.min(1, 720 / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        try {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(pixels.data, pixels.width, pixels.height);
          if (code?.data) {
            const path = getFacilityQrScanPath(code.data, window.location.origin);
            if (path) {
              stop();
              onScan(path);
              return;
            }
            setMessage("invalidScan");
          }
        } catch {
          stop();
          setMessage("cameraError");
          return;
        }
      }
      frame = requestAnimationFrame(readFrame);
    };
    const start = async () => {
      if (window.isSecureContext === false) {
        setMessage("cameraHttps");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia || !context) {
        setMessage("cameraUnavailable");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" } },
        });
        if (!active) { stream.getTracks().forEach((track) => track.stop()); return; }
        video.srcObject = stream;
        await video.play();
        if (!active) return;
        setMessage("cameraGuide");
        frame = requestAnimationFrame(readFrame);
      } catch (error) {
        if (!active) return;
        stop();
        setMessage(error.name === "NotAllowedError" ? "cameraDenied"
          : error.name === "NotFoundError" ? "cameraUnavailable" : "cameraError");
      }
    };
    const onHidden = () => {
      if (document.hidden) { stop(); onClose(); }
    };
    document.addEventListener("visibilitychange", onHidden);
    start();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [onScan, onClose]);

  return (
    <section className="facilityQrScanner" aria-label={t("facilityQr.scanNext")}>
      <video ref={videoRef} autoPlay muted playsInline aria-label={t("facilityQr.cameraGuide")} />
      <p role="status">{t(`facilityQr.${message}`)}</p>
      <button type="button" className="btn-bg-blue" onClick={onClose}>{t("facilityQr.stopScan")}</button>
    </section>
  );
}
